/** Provides the signed-in user's Preferences: cache-first, optimistic writes, merged server-side so devices do not clobber each other. */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { MotionConfig } from 'motion/react';
import { useAuth } from '../../platform/auth/AuthContext';
import {
  DEFAULT_PREFERENCES,
  applyPatch,
  mergePatches,
  sanitisePatch,
  type PreferencesPatch,
  type UserPreferences,
} from './preferencesSchema';
import { fetchPreferences, mergePreferences, readCache, writeCache } from './preferencesService';
import { getPreferencesSnapshot, setPreferencesSnapshot, subscribePreferences } from './preferencesStore';
import { usePreferenceEffects } from './usePreferenceEffects';
import { useThemeSync } from './useThemePreference';

export { getPreferencesSnapshot, subscribePreferences } from './preferencesStore';

export interface PreferencesContextValue {
  preferences: UserPreferences;
  /** False until the first server read settles; the cached or default values are already usable. */
  loaded: boolean;
  update: (patch: PreferencesPatch) => void;
  saveStatus: 'idle' | 'saving' | 'error';
  saveError: string;
  retrySave: () => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

let warnedSaveFailure = false;

const SAVE_RETRY_BASE_MS = 2_000;
const SAVE_RETRY_MAX_MS = 30_000;
const MAX_SAVE_RETRIES = 6;

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const preferences = useSyncExternalStore(subscribePreferences, getPreferencesSnapshot, getPreferencesSnapshot);
  const [loadedFor, setLoadedFor] = useState('');
  // The user whose server row has been read at least once; a theme is only canonical once a row exists.
  const [syncedFor, setSyncedFor] = useState('');
  const [saveState, setSaveState] = useState({owner: userId, status: 'idle' as 'idle' | 'saving' | 'error', error: ''});
  const { reduceMotion } = usePreferenceEffects();
  useThemeSync(syncedFor === userId && userId !== '');

  const pendingRef = useRef<PreferencesPatch>({});
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptRef = useRef(0);
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  // Bumped by every local edit so a read that started before it cannot overwrite it on arrival.
  const editVersionRef = useRef(0);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const clearRetry = useCallback(() => {
    if (retryTimerRef.current !== null) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
  }, []);

  const flush = useCallback(() => {
    flushTimerRef.current = null;
    clearRetry();
    const patch = pendingRef.current;
    if (Object.keys(patch).length === 0) return;
    pendingRef.current = {};
    const owner = userIdRef.current;
    inFlightRef.current += 1;
    setSaveState({owner, status: 'saving', error: ''});
    // Serialised so a later patch can never be overtaken by an earlier one.
    chainRef.current = chainRef.current.then(async () => {
      try {
        // Checked as late as possible: the account may have switched while this waited its turn.
        if (owner !== userIdRef.current) return;
        const merged = await mergePreferences(patch);
        retryAttemptRef.current = 0;
        if (owner === userIdRef.current && inFlightRef.current === 1 && Object.keys(pendingRef.current).length === 0) setSaveState({owner, status: 'idle', error: ''});
        if (merged && owner === userIdRef.current && inFlightRef.current === 1 && Object.keys(pendingRef.current).length === 0) {
          setPreferencesSnapshot(merged);
          writeCache(owner, merged);
        }
      } catch (error) {
        // The optimistic value stays on screen and in the cache; warn once, not per keystroke.
        if (!warnedSaveFailure) {
          warnedSaveFailure = true;
          console.warn('[Preferences] save failed, keeping local value:', error);
        }
        if (owner === userIdRef.current) {
          setSaveState({owner, status: 'error', error: 'Your changes are kept on this device but could not be synced.'});
          // Newer edits made since win over the failed ones; the pending patch doubles as the dirty marker.
          pendingRef.current = mergePatches(patch, pendingRef.current);
          if (retryAttemptRef.current < MAX_SAVE_RETRIES && retryTimerRef.current === null && flushTimerRef.current === null) {
            const delay = Math.min(SAVE_RETRY_BASE_MS * 2 ** retryAttemptRef.current, SAVE_RETRY_MAX_MS);
            retryAttemptRef.current += 1;
            retryTimerRef.current = setTimeout(flush, delay);
          }
        }
      } finally {
        inFlightRef.current -= 1;
      }
    });
  }, [clearRetry]);

  const update = useCallback((patch: PreferencesPatch) => {
    const clean = sanitisePatch(patch);
    if (Object.keys(clean).length === 0) return;
    editVersionRef.current += 1;
    setSaveState({owner: userIdRef.current, status: 'saving', error: ''});
    const next = applyPatch(getPreferencesSnapshot(), clean);
    setPreferencesSnapshot(next);
    writeCache(userIdRef.current, next);
    pendingRef.current = mergePatches(pendingRef.current, clean);
    if (flushTimerRef.current === null) flushTimerRef.current = setTimeout(flush, 0);
  }, [flush]);

  // Declared first so on unmount it runs before the per-user cleanup below discards pending patches.
  useEffect(() => () => {
    clearRetry();
    if (flushTimerRef.current !== null) {
      clearTimeout(flushTimerRef.current);
      flush();
    }
  }, [flush, clearRetry]);

  useEffect(() => {
    setSaveState({owner: userId, status: 'idle', error: ''});
    setPreferencesSnapshot(userId ? readCache(userId) ?? DEFAULT_PREFERENCES : DEFAULT_PREFERENCES);
    if (!userId) return;

    let cancelled = false;
    const load = async () => {
      try {
        const versionAtStart = editVersionRef.current;
        const stored = await fetchPreferences(userId);
        if (cancelled || !stored) return;
        if (editVersionRef.current !== versionAtStart || inFlightRef.current > 0) return;
        // Edits that failed to save are still pending: lay them over the server row rather than losing them.
        const unsent = pendingRef.current;
        const next = Object.keys(unsent).length > 0 ? applyPatch(stored, unsent) : stored;
        setPreferencesSnapshot(next);
        writeCache(userId, next);
        setSyncedFor(userId);
      } catch (error) {
        console.warn('[Preferences] load failed, keeping cached values:', error);
      } finally {
        if (!cancelled) setLoadedFor(userId);
      }
    };
    void load();

    const onFocus = () => {
      void load();
      // Back in front: give unsent edits another go instead of waiting out the backoff.
      if (Object.keys(pendingRef.current).length > 0 && inFlightRef.current === 0 && flushTimerRef.current === null) {
        retryAttemptRef.current = 0;
        flush();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      // A patch queued for the previous account must never reach the next one.
      if (flushTimerRef.current !== null) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
      clearRetry();
      retryAttemptRef.current = 0;
      pendingRef.current = {};
    };
  }, [userId, flush, clearRetry]);

  const retrySave = useCallback(() => {
    retryAttemptRef.current = 0;
    if (flushTimerRef.current !== null) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = null;
    flush();
  }, [flush]);
  const saveStatus = saveState.owner === userId ? saveState.status : 'idle';
  const saveError = saveState.owner === userId ? saveState.error : '';
  const value = useMemo<PreferencesContextValue>(
    () => ({ preferences, loaded: loadedFor === userId && userId !== '', update, saveStatus, saveError, retrySave }),
    [preferences, loadedFor, userId, update, saveStatus, saveError, retrySave],
  );

  return (
    <PreferencesContext.Provider value={value}>
      <MotionConfig reducedMotion={reduceMotion ? 'always' : 'user'}>{children}</MotionConfig>
    </PreferencesContext.Provider>
  );
}

PreferencesProvider.displayName = 'PreferencesProvider';

function updateLocalOnly(patch: PreferencesPatch): void {
  setPreferencesSnapshot(applyPatch(getPreferencesSnapshot(), sanitisePatch(patch)));
}

/** Without a provider (isolated renders and tests) preferences still read and change in memory. */
export function usePreferences(): PreferencesContextValue {
  const context = useContext(PreferencesContext);
  const preferences = useSyncExternalStore(subscribePreferences, getPreferencesSnapshot, getPreferencesSnapshot);
  return useMemo(
    () => context ?? { preferences, loaded: false, update: updateLocalOnly, saveStatus: 'idle', saveError: '', retrySave: () => {} },
    [context, preferences],
  );
}
